import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, Monitor, Tablet, Smartphone, Check, Layers,
  ShoppingBag, Eye, Pencil, ExternalLink, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import LivePreviewFrame from '@/components/LivePreviewFrame';
import AuthModal from '@/components/AuthModal';
import CheckoutDialog from '@/components/checkout/CheckoutDialog';
import SellSiteDialog from '@/components/marketplace/SellSiteDialog';
import ListingCard from '@/components/marketplace/ListingCard';
import { useAuth } from '@/context/AuthContext';
import {
  getListing, getListingPreview, canBuyListing, browseListings, formatListingPrice, LISTING_STATUS,
} from '@/lib/marketplace';
import { categoryLabel } from '@/config/earnings';
import { toPages, homePath, pageLabel } from '@/lib/sitePages';

// ─── /marketplace/:listingId ──────────────────────────────────────────────────
// One site for sale: the whole site, live, page by page and at three screen
// sizes, beside what it costs and what the buyer gets. A buyer should be able
// to judge the site completely before paying for it.

const RETURN_ERRORS = {
  amount_mismatch: 'The payment did not match the price, so the site was not delivered. If you were charged, contact support.',
  payment_not_verified: 'That payment did not go through. Nothing was charged — you can try again.',
};

const DEVICES = [
  { id: 'desktop', icon: Monitor, width: '100%' },
  { id: 'tablet', icon: Tablet, width: '820px' },
  { id: 'mobile', icon: Smartphone, width: '390px' },
];

export default function MarketplaceListing() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [site, setSite] = useState(null);
  const [error, setError] = useState('');
  const [activePath, setActivePath] = useState(null);
  const [device, setDevice] = useState('desktop');
  const [checkout, setCheckout] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setData(null); setSite(null); setError(''); setActivePath(null);
    getListing(listingId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'This listing is not available.'); });
    getListingPreview(listingId)
      .then((s) => { if (!cancelled) setSite(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [listingId, user?.id]);

  // Back from the gateway with a problem: say what happened.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err) toast.error(RETURN_ERRORS[err] || 'That payment could not be completed.');
    if (params.get('pending')) toast.info('Your transfer is being confirmed — the site lands in your account as soon as it clears.');
    if (err || params.get('pending')) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const listing = data?.listing;

  useEffect(() => {
    if (!listing?.category) return undefined;
    let cancelled = false;
    browseListings({ category: listing.category, exclude: listing.id, limit: 3 })
      .then((d) => { if (!cancelled) setRelated(d.listings.map(l => ({ ...l, pricing: { symbol: d.symbol, locale: d.locale } }))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [listing?.category, listing?.id]);

  const pages = useMemo(() => toPages(site || {}), [site]);
  useEffect(() => {
    if (pages.length && !activePath) setActivePath(homePath(pages));
  }, [pages, activePath]);

  const priceText = data ? formatListingPrice(data.price.amount, data.price) : '';

  const buy = async () => {
    if (!user) { setAuthOpen(true); return; }
    setChecking(true);
    try {
      const { price } = await canBuyListing(listing.id);
      setCheckout({
        purpose: 'marketplace',
        listingId: listing.id,
        title: listing.title,
        subtitle: 'One payment · yours to keep',
        price: price.amount,
        pricing: price,
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChecking(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-28 px-4 text-center">
        <AlertTriangle className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
        <h1 className="text-white text-xl font-bold">This listing is not available</h1>
        <p className="text-zinc-500 text-sm mt-2">{error}</p>
        <Link to="/Marketplace" className="mt-6 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-white text-zinc-950 text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to the marketplace
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-28 flex items-start justify-center">
        <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin text-orange-500" /> Loading listing…</div>
      </div>
    );
  }

  const status = LISTING_STATUS[listing.status];
  const deviceWidth = DEVICES.find(d => d.id === device)?.width || '100%';

  return (
    <div className="bg-zinc-950 min-h-screen">
      <SEOHead
        title={`${listing.title} — Marketplace`}
        description={listing.description?.slice(0, 155) || `A ready-made ${categoryLabel(listing.category).toLowerCase()} website on the BuildersLab Marketplace.`}
        ogUrl={`https://builderslab.net/marketplace/${listing.id}`}
      />
      <div className="pt-24 sm:pt-28 pb-16 max-w-7xl mx-auto px-4 sm:px-6">
        <Link to="/Marketplace" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Marketplace
        </Link>

        {listing.isOwner && status && (
          <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3 ${status.tone}`}>
            <span>
              This is your listing — <span className="font-semibold">{status.label}</span>.
              {listing.status === 'pending' && ' It shows on the marketplace once it has been reviewed.'}
              {listing.status === 'rejected' && listing.rejection_reason && ` ${listing.rejection_reason}`}
            </span>
            <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 font-semibold underline-offset-2 hover:underline">
              <Pencil className="w-3.5 h-3.5" /> Edit listing
            </button>
          </div>
        )}

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 items-start">
          {/* ── The site, live ── */}
          <div className="min-w-0">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
              <div className="h-11 flex items-center justify-between gap-2 px-3 border-b border-zinc-800">
                <div className="flex items-center gap-1 overflow-x-auto min-w-0">
                  {pages.length > 1 && pages.map(p => (
                    <button key={p.path} onClick={() => setActivePath(p.path)}
                      className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        activePath === p.path ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800'
                      }`}>
                      {pageLabel(p.path)}
                    </button>
                  ))}
                  {pages.length <= 1 && <span className="text-xs text-zinc-500 px-1">Live preview</span>}
                </div>
                <div className="hidden md:flex items-center gap-1 shrink-0">
                  {DEVICES.map(({ id, icon: Icon }) => (
                    <button key={id} onClick={() => setDevice(id)} aria-label={`${id} view`} aria-pressed={device === id}
                      className={`p-1.5 rounded-lg ${device === id ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-zinc-950 flex justify-center">
                <div className="bg-snow h-[60vh] sm:h-[70vh] transition-[width] duration-300 max-w-full" style={{ width: deviceWidth }}>
                  {site ? (
                    <LivePreviewFrame
                      title={`${listing.title} preview`}
                      pages={pages}
                      css={site.css}
                      js={site.js}
                      icon={site.icon}
                      activePath={activePath ?? homePath(pages)}
                      onNavigate={setActivePath}
                      isGenerating={false}
                      className="w-full h-full border-none"
                    />
                  ) : (
                    <div className="w-full h-full grid place-items-center bg-zinc-950">
                      <Loader2 className="w-6 h-6 text-zinc-600 animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {listing.description && (
              <div className="mt-6">
                <h2 className="text-white font-semibold">About this site</h2>
                <p className="mt-2 text-zinc-400 text-[15px] leading-relaxed whitespace-pre-line">{listing.description}</p>
              </div>
            )}
          </div>

          {/* ── What it costs, what you get ── */}
          <aside className="lg:sticky lg:top-24 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-400">{categoryLabel(listing.category)}</p>
            <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-white leading-tight">{listing.title}</h1>
            <p className="mt-1 text-sm text-zinc-500">by {listing.seller?.name}</p>

            <div className="mt-5 flex items-end justify-between gap-3">
              <span className="text-4xl font-extrabold tracking-tight text-white">{priceText}</span>
              <span className="text-xs text-zinc-500 mb-1">one payment</span>
            </div>

            {listing.isOwner ? (
              <button onClick={() => setEditing(true)}
                className="mt-4 w-full h-12 rounded-full bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-sm font-bold text-white inline-flex items-center justify-center gap-2">
                <Pencil className="w-4 h-4" /> Edit listing
              </button>
            ) : data.ownedWebsiteId ? (
              <button onClick={() => navigate(`/build/${data.ownedWebsiteId}`)}
                className="mt-4 w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-sm font-bold text-white inline-flex items-center justify-center gap-2">
                <ExternalLink className="w-4 h-4" /> You own this — open it
              </button>
            ) : (
              <button onClick={buy} disabled={checking || listing.status !== 'active'}
                className="mt-4 w-full h-12 rounded-full bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-sm font-bold text-ink inline-flex items-center justify-center gap-2">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                {listing.status === 'active' ? `Buy now — ${priceText}` : 'No longer for sale'}
              </button>
            )}

            <ul className="mt-5 space-y-2.5 text-sm text-zinc-300">
              {[
                'Your own copy, in your BuildersLab account',
                'Full HTML, CSS and JS — download or export to React',
                'No BuildersLab badge',
                'Edit it with prompts or by clicking, then publish',
              ].map(item => (
                <li key={item} className="flex items-start gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />{item}</li>
              ))}
            </ul>

            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-zinc-950 border border-zinc-800 py-2.5">
                <Layers className="w-4 h-4 text-zinc-500 mx-auto" />
                <p className="mt-1 text-sm font-bold text-white">{listing.page_count || 1}</p>
                <p className="text-[10px] text-zinc-600">pages</p>
              </div>
              <div className="rounded-xl bg-zinc-950 border border-zinc-800 py-2.5">
                <ShoppingBag className="w-4 h-4 text-zinc-500 mx-auto" />
                <p className="mt-1 text-sm font-bold text-white">{listing.sales_count || 0}</p>
                <p className="text-[10px] text-zinc-600">sold</p>
              </div>
              <div className="rounded-xl bg-zinc-950 border border-zinc-800 py-2.5">
                <Eye className="w-4 h-4 text-zinc-500 mx-auto" />
                <p className="mt-1 text-sm font-bold text-white">{listing.views || 0}</p>
                <p className="text-[10px] text-zinc-600">views</p>
              </div>
            </div>

            <p className="mt-5 flex items-start gap-2 text-xs text-zinc-500 leading-relaxed">
              <ShieldCheck className="w-4 h-4 text-zinc-600 shrink-0" />
              {listing.license === 'exclusive'
                ? 'Exclusive: sold once. The listing closes after this sale.'
                : 'Template licence: other buyers can buy this design too.'}
              {' '}Paid by card, bank, mobile money or crypto.
            </p>
          </aside>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="text-white font-bold text-xl mb-5">More {categoryLabel(listing.category).toLowerCase()} sites</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map(l => <ListingCard key={l.id} listing={l} pricing={l.pricing} />)}
            </div>
          </div>
        )}
      </div>

      {checkout && <CheckoutDialog purchase={checkout} onClose={() => setCheckout(null)} />}
      <AuthModal isOpen={authOpen} onClose={setAuthOpen} description="Sign in to buy this site — it goes straight into your account." />
      {listing.isOwner && (
        <SellSiteDialog
          open={editing}
          onClose={() => setEditing(false)}
          listing={{ ...listing, website_id: listing.website_id }}
          onSaved={(saved) => setData(d => ({ ...d, listing: { ...d.listing, ...saved } }))}
        />
      )}
    </div>
  );
}
